import { useCallback, useEffect, useRef, useState } from 'react'
import { studentsPasswordOverride } from '@/api/client'
import type { OverrideStudentPasswordPreview, OverrideStudentPasswordReasonEnum } from '@/api/model'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  observedTraceCopy,
  overrideConflictCode,
  overrideErrorMessage,
  overrideReasons,
  overrideRefreshCodes,
  traceExplanations,
} from './reset-password-copy'

export type OverrideResult = { login: string; temporaryPassword: string; expiresAt: string }

function defaultReason(preview: OverrideStudentPasswordPreview) {
  if (!preview.recovery.configured) return 'recovery_not_configured'
  if (preview.recovery.locked) return 'recovery_locked'
  return ''
}

function recoveryLabel({ configured, locked }: OverrideStudentPasswordPreview['recovery']) {
  if (!configured) return 'nie ustawione'
  return locked ? 'zablokowane po nieudanych próbach' : 'ustawione'
}

/**
 * Reset hasła ucznia przez superadministratora bez odpowiedzi na pytanie bezpieczeństwa.
 * Wywołania są imperatywne, a nie przez mutację TanStack: mutacja trzymałaby hasło
 * superadministratora i hasło tymczasowe w pamięci podręcznej. Hasło żyje wyłącznie w stanie
 * tego komponentu i znika razem z nim; numer generacji odrzuca spóźnione odpowiedzi.
 */
export function PasswordOverrideForm({
  login,
  onDone,
  onSessionEnded,
  onCancel,
  onBusyChange,
}: {
  login: string
  onDone: (result: OverrideResult) => void
  /** Piąta błędna próba hasła (401): sesja superadministratora została zakończona. */
  onSessionEnded?: () => void
  onCancel: () => void
  onBusyChange?: (busy: boolean) => void
}) {
  const [preview, setPreview] = useState<OverrideStudentPasswordPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [typedLogin, setTypedLogin] = useState('')
  const generation = useRef(0)
  const sessionEnded = useRef(onSessionEnded)

  useEffect(() => {
    sessionEnded.current = onSessionEnded
  }, [onSessionEnded])

  useEffect(() => {
    onBusyChange?.(loading || submitting)
  }, [loading, submitting, onBusyChange])

  useEffect(() => {
    const current = generation
    return () => {
      current.current += 1
    }
  }, [])

  const fetchPreview = useCallback(() => {
    const ticket = ++generation.current
    return studentsPasswordOverride({ login })
      .then((res) => {
        if (ticket !== generation.current) return
        if (res.status === 200 && res.data.outcome === 'preview') {
          const loaded = res.data
          setPreview(loaded)
          setReason((prev) => prev || defaultReason(loaded))
        } else if (res.status === 401) {
          sessionEnded.current?.()
        } else {
          setError(overrideErrorMessage(res.status, res.data))
        }
        setLoading(false)
      })
      .catch(() => {
        if (ticket !== generation.current) return
        setError(overrideErrorMessage(0, null))
        setLoading(false)
      })
  }, [login])

  // Powtórny podgląd (odświeżenie po zmianie stanu konta) czyści też wpisane dane.
  const loadPreview = (message?: string) => {
    setLoading(true)
    setPreview(null)
    setError(message ?? null)
    setPassword('')
    setTypedLogin('')
    return fetchPreview()
  }

  useEffect(() => {
    void fetchPreview()
  }, [fetchPreview])

  const loginMatches = typedLogin.trim().toUpperCase() === login.toUpperCase()
  const ready = preview !== null && password.length > 0 && reason !== '' && loginMatches

  const submit = async () => {
    if (!preview || !ready) return
    const ticket = ++generation.current
    setSubmitting(true)
    setError(null)
    const sentPassword = password
    setPassword('')
    try {
      const res = await studentsPasswordOverride({
        login,
        confirm: true,
        password: sentPassword,
        reason: reason as OverrideStudentPasswordReasonEnum,
        login_confirmation: typedLogin.trim(),
        preview_token: preview.preview_token,
        acknowledge_recent_activity: true,
      })
      if (ticket !== generation.current) return
      if (res.status === 200 && res.data.outcome === 'reset') {
        onDone({
          login: res.data.login,
          temporaryPassword: res.data.temporary_password,
          expiresAt: res.data.expires_at,
        })
        return
      }
      setSubmitting(false)
      if (res.status === 401) {
        sessionEnded.current?.()
        return
      }
      const message = overrideErrorMessage(res.status, res.data)
      const code = res.status === 409 ? overrideConflictCode(res.data) : null
      if (code && overrideRefreshCodes.includes(code)) {
        await loadPreview(message)
        return
      }
      setError(message)
    } catch {
      if (ticket !== generation.current) return
      setSubmitting(false)
      setError(overrideErrorMessage(0, null))
    }
  }

  const busy = loading || submitting

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      {loading && <p>Sprawdzanie konta…</p>}
      {preview && (
        <>
          <div className="flex flex-col gap-2 rounded-md border px-3 py-2 text-sm">
            <dl className="flex flex-col gap-1">
              {[
                ['Ostatni ślad aktywności', observedTraceCopy[preview.observed_activity]],
                ['Aktywna sesja', preview.has_live_session ? 'tak' : 'nie'],
                ['Przypisane urządzenie', preview.has_device ? 'tak' : 'nie'],
                ['Pytanie bezpieczeństwa', recoveryLabel(preview.recovery)],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-3">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="text-right font-medium">{value}</dd>
                </div>
              ))}
            </dl>
            <ul className="flex flex-col gap-1.5 border-t pt-2 text-xs text-muted-foreground">
              {traceExplanations.map(({ term, text }) => (
                <li key={term}>
                  <span className="font-medium text-foreground">{term}</span> – {text}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            Reset usunie pytanie bezpieczeństwa i zaufane urządzenia ucznia. Po zalogowaniu uczeń
            zmieni hasło i ustawi pytanie od nowa.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="override-password">Twoje hasło</Label>
            <Input
              id="override-password"
              type="password"
              autoComplete="current-password"
              disabled={busy}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <fieldset className="flex flex-col gap-2" disabled={busy}>
            <legend className="mb-1 text-sm font-medium">Powód</legend>
            <RadioGroup value={reason} onValueChange={setReason}>
              {overrideReasons.map((option) => (
                <div key={option.value} className="flex items-center gap-2">
                  <RadioGroupItem value={option.value} id={`override-reason-${option.value}`} />
                  <Label htmlFor={`override-reason-${option.value}`}>{option.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </fieldset>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="override-target-check">Potwierdź konto ucznia</Label>
            <Input
              id="override-target-check"
              name="override-target-check"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
              placeholder={login}
              disabled={busy}
              value={typedLogin}
              onChange={(event) => setTypedLogin(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Wpisz <span className="font-mono">{login}</span>, żeby mieć pewność, że resetujesz
              właściwe konto.
            </p>
          </div>
        </>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" disabled={submitting} onClick={onCancel}>
          Wróć
        </Button>
        {!loading && !preview && (
          <Button type="button" variant="outline" onClick={() => void loadPreview()}>
            Odśwież podgląd
          </Button>
        )}
        {preview && (
          <Button type="submit" disabled={busy || !ready}>
            {submitting ? 'Resetowanie…' : 'Resetuj hasło'}
          </Button>
        )}
      </div>
    </form>
  )
}
